import React from 'react';
import { STATUS_COLORS, STATUS_LABELS } from '../constants/colors';
import StatusPill from './ui/StatusPill';
import { daysDiff } from '../utils/ganttDates';

/**
 * Shared StatusBadge component.
 *
 * Renders a pill badge styled with colours from the shared STATUS_COLORS map.
 * Accepts optional className, size, and data-testid overrides.
 *
 * When the optional `order` prop is supplied and the order is in a terminal
 * status (delivered / cancelled), the badge picks up a hover tooltip showing
 * when the status was last set — e.g. "Delivered 26/05/26 — 2 days ago".
 * We use `order.updated_at` as the best-effort proxy for when the status
 * flipped (no dedicated `delivered_at` column exists today). If the order is
 * edited after delivery this would skew the tooltip, but in practice
 * delivered orders are rarely edited.
 *
 * @param {string} status   - Order status key (e.g. 'submitted', 'approved')
 * @param {object} [order]  - Optional order object; used to derive the hover tooltip
 * @param {string} [size]   - 'sm' (default) or 'md' for larger badges
 * @param {string} [className] - Additional classes to merge
 * @param {object} [rest]   - Spread onto the <span> (e.g. data-testid)
 */
export default function StatusBadge({ status, order, size, className = '', ...rest }) {
  const colorClasses = STATUS_COLORS[status] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  const label = STATUS_LABELS[status] || (status ? status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ') : '-');

  const tooltip = buildStatusTooltip(status, order);

  return <StatusPill label={label} colorClass={colorClasses} size={size} className={className} title={tooltip} {...rest} />;
}

function buildStatusTooltip(status, order) {
  if (!order || !order.updated_at) return undefined;
  const verb = STATUS_TOOLTIP_VERB[status];
  if (!verb) return undefined;
  const when = new Date(order.updated_at);
  if (Number.isNaN(when.getTime())) return undefined;
  const formattedDate = when.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const whenDay = new Date(when);
  whenDay.setHours(0, 0, 0, 0);
  // 🕰️ ROUNDED via `daysDiff`, never floored: two local midnights across the
  // spring clock change are 23 h apart and floor said "today" for yesterday
  // (`hires/hireDays.clockChange.test.js`).
  const daysAgo = daysDiff(whenDay, today);
  let agoText;
  if (daysAgo === 0) agoText = 'today';
  else if (daysAgo === 1) agoText = '1 day ago';
  else if (daysAgo < 0) agoText = 'just now';
  else agoText = `${daysAgo} days ago`;
  return `${verb} ${formattedDate} — ${agoText}`;
}

const STATUS_TOOLTIP_VERB = {
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  ordered: 'Ordered',
  partially_delivered: 'Partially delivered',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};
